import { Select } from '@/components/UI/FormFields/Latest';
import TagsInput from '@/components/UI/TagsInput/TagsInput';
import { ErrorMessage } from '@hookform/error-message';
import type { TaskScheduleConditionalType } from '@/model/Task.model';
import { FC, useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form-latest';
import { RiInformationLine } from 'react-icons/ri';
import { StepFormPayloadProps, StepRenderProps } from '../../AddTask.model';
import {
  conditionalAnimalAgeValidator,
  conditionalStudyDayValidator,
  conditionalTrackingDayValidator,
  conditionalValueValidator,
} from './AddTaskScheduleForm.utils';
import { buildTags } from '@/components/Modals/AddTask/Steps/AddTaskScheduleForm/AddTaskScheduleConditionalFields.utils.ts';
import { DurationPicker } from '@/components/UI/DurationPicker/DurationPicker';

export type ConditionalFieldsProps = Pick<StepRenderProps, 'disabledFields'>;

export const conditionTypeOptions: Array<{
  label: string;
  value: TaskScheduleConditionalType;
}> = [
  { label: 'Age (days)', value: 'age' },
  { label: 'Study day', value: 'study_day' },
  { label: 'Tracking day', value: 'tracking_day' },
];

const ConditionalFields: FC<ConditionalFieldsProps> = ({ disabledFields }) => {
  const { control, formState, trigger } = useFormContext<StepFormPayloadProps>();
  const type = useWatch({ control, name: 'schedule.condition.type' });
  useEffect(() => {
    if (type) {
      trigger('schedule.condition.value');
    }
  }, [type]);

  return (
    <div className="ml4 mt3">
      <div
        className="flex items-center mb2"
        data-test-component="ConditionalScheduleForm"
        data-test-element="condition-type"
      >
        <Select
          options={conditionTypeOptions}
          name="schedule.condition.type"
          required={true}
          disabled={disabledFields?.has('schedule.condition.type') ?? false}
        />
        <span className="f6 near-black lh-solid dib ml3">is</span>
      </div>
      <ConditionalTypeInformation type={type} />
      <div className="mw6 mb2" data-test-component="ConditionalScheduleForm" data-test-element="condition-value">
        <Controller
          control={control}
          name="schedule.condition.value"
          defaultValue={[]}
          rules={{
            validate: {
              isEmpty: conditionalValueValidator,
              typeValueValiadtor: (value: Array<number> | undefined) => {
                if (!value) {
                  return 'At least one value is required';
                }
                switch (type) {
                  case 'age':
                    return conditionalAnimalAgeValidator(value);
                  case 'study_day':
                    return conditionalStudyDayValidator(value);
                  case 'tracking_day':
                    return conditionalTrackingDayValidator(value);
                  default:
                    return 'No valid type set';
                }
              },
            },
          }}
          render={({ field }) => (
            <TagsInput
              value={field.value?.map((v) => `${v}`) ?? []}
              onChange={(tags) => field.onChange(buildTags(tags))}
              maxTags={365}
              onlyUnique
              className="ba b--moon-gray bg-white shadow-none"
              testId="schedule-condition-value-tags"
              insideTagsInput
            />
          )}
        />
        <p className="mt2 f7 near-black">Separate values with commas, Tab key or Enter key</p>
        <ErrorMessage
          errors={formState.errors}
          name="schedule.condition.value"
          render={({ message }) => <p className="f7 red db pv2">{message}</p>}
        />
        <div className="mt3">
          <label className="w4 mb2 lh-title">Duration</label>
          <Controller
            name="schedule.time"
            control={control}
            defaultValue={{ type: 'all_day' }}
            render={({ field }) => (
              <DurationPicker
                value={field.value ?? { type: 'all_day' }}
                disabled={field.disabled}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

const ConditionalTypeInformation: FC<{ type: TaskScheduleConditionalType | undefined }> = ({ type }) => {
  switch (type) {
    case 'tracking_day':
      return (
        <span
          className="bg-washed-blue near-black flex justify-around f6 pa2 items-center mv2"
          style={{ maxWidth: '355px' }}
        >
          <RiInformationLine />
          <span>The animal&apos;s tracking date will be used as day 0</span>
        </span>
      );
    case 'study_day':
      return (
        <span
          className="bg-washed-blue near-black flex justify-around f6 pa2 items-center mv2"
          style={{ maxWidth: '315px' }}
        >
          <RiInformationLine />
          <span>The study start date will be used as Day 1</span>
        </span>
      );
    default:
      return null;
  }
};

export default ConditionalFields;
